import { useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

/**
 * Hook that manages proactive token renewal for Microsoft Graph and FLEHS APIs.
 *
 * PURPOSE:
 * Azure AD access tokens expire after ~1 hour. Rather than waiting for API calls
 * to fail, this hook proactively renews tokens in the background to ensure
 * seamless user experience. If renewal fails due to expired refresh token or
 * revoked access, the user is immediately redirected to login BEFORE they can
 * start entering data that would be lost.
 *
 * TIMING:
 * - Initial check: 5 seconds after mount (catches already-expired sessions)
 * - Recurring: Every 30 minutes (keeps tokens fresh during long sessions)
 *
 * ERROR HANDLING:
 * - InteractionRequiredAuthError: Clears MSAL cache and redirects to home page
 *   (forces re-authentication before user enters data)
 * - Other errors: Logged but not fatal (next renewal or API call will retry)
 */
export function useTokenRenewal() {
  // instance: MSAL client for token operations
  // accounts: Array of signed-in accounts (empty = not logged in)
  // inProgress: MSAL operation state ("none" when idle, prevents concurrent auth)
  const { instance, accounts, inProgress } = useMsal();

  /**
   * Renews the Microsoft Graph API access token.
   * Used for user profile photo, email, and other Graph operations.
   *
   * @param forceRefresh - If true, bypasses cache and forces network request
   * @returns Token response if successful, undefined if preconditions not met
   */
  const renewGraphToken = useCallback(
    async (forceRefresh = false) => {
      // Guard: Skip if no user logged in or MSAL is busy with another operation
      // inProgress !== "none" prevents race conditions with login/logout flows
      if (!accounts || accounts.length === 0 || inProgress !== "none") {
        return;
      }

      // Use first account (single-account app pattern)
      const activeAccount = accounts[0];

      try {
        // Build silent token request for Microsoft Graph
        const silentRequest = {
          scopes: ["https://graph.microsoft.com/User.Read"], // Graph API scope
          account: activeAccount, // Which user to get token for
          forceRefresh, // true = skip cache, hit Azure AD directly
        };

        // Attempt silent token acquisition:
        // 1. Checks MSAL cache for valid token (instant, no network)
        // 2. If expired, uses refresh token to get new access token (network call)
        // 3. If refresh token invalid, throws InteractionRequiredAuthError
        const response = await instance.acquireTokenSilent(silentRequest);
        return response;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          // Refresh token expired/revoked, or Azure AD requires re-authentication
          // (password changed, MFA required, Conditional Access policy, etc.)

          // Clear all MSAL state so app recognizes user as logged out
          instance.setActiveAccount(null); // Remove "current user" reference
          instance.clearCache(); // Remove all cached tokens from browser storage

          // Redirect to home page - forces full React state reset and shows login
          // This happens BEFORE user starts entering data, preventing data loss
          window.location.href = "/";
          throw error;
        } else {
          // Network errors, timeouts, etc. - log but don't redirect
          // Next scheduled renewal or API call will retry
          console.error("Silent Graph token renewal failed", error);
          throw error;
        }
      }
    },
    [instance, accounts, inProgress] // Re-create callback if MSAL state changes
  );

  /**
   * Renews the FLEHS API access token.
   * Used for all backend API operations (sentinel, mosquito, veterinary, admin).
   *
   * @param forceRefresh - If true, bypasses cache and forces network request
   * @returns Token response if successful, undefined if preconditions not met
   */
  const renewFlehsToken = useCallback(
    async (forceRefresh = false) => {
      // Guard: Skip if no user logged in or MSAL is busy with another operation
      if (!accounts || accounts.length === 0 || inProgress !== "none") {
        return;
      }

      // Use first account (single-account app pattern)
      const activeAccount = accounts[0];

      try {
        // Build silent token request for FLEHS API
        const silentRequest = {
          scopes: ["api://5b9d1c95-aa2d-4545-af3f-72cc38b875f4/Flehs.User"], // FLEHS API scope
          account: activeAccount, // Which user to get token for
          forceRefresh, // true = skip cache, hit Azure AD directly
        };

        // Attempt silent token acquisition (same flow as Graph token above)
        const response = await instance.acquireTokenSilent(silentRequest);
        return response;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          // Refresh token expired/revoked - clear cache and redirect to force re-login
          instance.setActiveAccount(null);
          instance.clearCache();
          window.location.href = "/";
          throw error;
        } else {
          // Non-auth errors - log but don't redirect
          console.error("Silent FLEHS token renewal failed", error);
          throw error;
        }
      }
    },
    [instance, accounts, inProgress] // Re-create callback if MSAL state changes
  );

  /**
   * Effect: Sets up proactive token renewal schedule.
   * Runs on mount and whenever renewal functions change (due to MSAL state changes).
   */
  useEffect(() => {
    // Guard: Don't schedule renewals if not logged in or MSAL is busy
    if (!accounts || accounts.length === 0 || inProgress !== "none") return;

    // RECURRING RENEWAL: Every 30 minutes
    // Keeps tokens fresh during long editing sessions
    // 30 min is well under the ~1 hour token lifetime, providing buffer
    const interval = setInterval(() => {
      // Renew Graph token - errors are caught to prevent unhandled rejection
      // If InteractionRequiredAuthError occurs, renewGraphToken redirects to login
      renewGraphToken().catch((error) => {
        console.error("Scheduled Graph token renewal failed:", error);
      });

      // Renew FLEHS token - same error handling pattern
      renewFlehsToken().catch((error) => {
        console.error("Scheduled FLEHS token renewal failed:", error);
      });
    }, 30 * 60 * 1000); // 30 minutes in milliseconds

    // INITIAL RENEWAL: 5 seconds after mount
    // Catches sessions where refresh token expired while user was away
    // Short delay allows app to fully initialize before token operations
    const timeout = setTimeout(() => {
      // Errors swallowed here - if token is still valid, renewal may "fail"
      // because there's nothing to renew (this is expected behavior)
      renewGraphToken().catch(() => {
        // Initial renewal failure is expected when token is still valid
      });

      renewFlehsToken().catch(() => {
        // Initial renewal failure is expected when token is still valid
      });
    }, 5000); // 5 seconds

    // Cleanup: Clear timers when component unmounts or dependencies change
    // Prevents memory leaks and duplicate renewal schedules
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [renewGraphToken, renewFlehsToken]); // Re-run if renewal functions change

  // Expose renewal functions for manual use (e.g., pre-flight checks before editing)
  return { renewGraphToken, renewFlehsToken };
}
